import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { CopyBtn } from '../components/CopyBtn'
import { Flash } from '../components/Flash'
import { api, formatVnd, type Order } from '../lib/api'
import { StatusBadge } from './AccountPage'

const POLL_STATUSES = ['chua_thanh_toan', 'cho_giao_hang']
const QUEUE_TYPES = ['buyitforme', 'cape']

export function CheckoutPage() {
  const { id } = useParams()
  const { user, loading, refresh } = useAuth()
  const nav = useNavigate()
  const [order, setOrder] = useState<Order | null>(null)
  const [err, setErr] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [gemBusy, setGemBusy] = useState(false)
  const [gemErr, setGemErr] = useState('')

  useEffect(() => {
    if (!id || !user) return
    let alive = true
    let timer: ReturnType<typeof setTimeout>
    const load = () =>
      api
        .order(Number(id))
        .then((r) => {
          if (!alive) return
          setOrder(r.order)
          const waiting =
            POLL_STATUSES.includes(r.order.status) ||
            (r.order.status === 'da_thanh_toan' &&
              !r.order.keys?.length &&
              !QUEUE_TYPES.includes(r.order.type))
          if (waiting) timer = setTimeout(load, 3000)
        })
        .catch((e) => alive && setErr(e.message))
    load()
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [id, user])

  async function copyKey(key: string) {
    try {
      await navigator.clipboard.writeText(key)
      setCopied(key)
      window.setTimeout(() => setCopied(null), 2000)
    } catch {
      /* ignore */
    }
  }

  async function payGem(o: Order) {
    if (!window.confirm(`Xác nhận trừ ${o.total.toLocaleString('vi-VN')} gem để đặt hàng?`)) return
    setGemErr('')
    setGemBusy(true)
    try {
      await api.payOrderGem(o.id)
      await refresh()
      nav('/account/dat-ho', { state: { flash: 'Thanh toán bằng ví gem thành công! Chờ shop giao hàng nhé.' } })
    } catch (ex) {
      setGemErr((ex as Error).message)
      setGemBusy(false)
    }
  }

  if (loading) return <p className="muted">…</p>
  if (!user) {
    return (
      <p className="note">
        Cần <Link to="/login">đăng nhập</Link> để xem hóa đơn.
      </p>
    )
  }
  if (err) return <p className="note">{err}</p>
  if (!order) return <p className="muted">Đang tải hóa đơn…</p>

  const pay = order.payment
  const keys =
    order.keys?.length
      ? order.keys
      : (order.items || []).map((it) => it.key || it.item_preview).filter(Boolean)
  const delivered = keys.length > 0
  const paid = ['da_thanh_toan', 'da_giao_hang', 'cho_giao_hang'].includes(order.status)
  const isBfm = order.type === 'buyitforme'
  const gem = user.balance2 ?? 0

  return (
    <div className="dep checkout">
      <Link className="okey-back" to="/account">
        <i className="fa-solid fa-arrow-left" aria-hidden /> Đơn hàng của tôi
      </Link>
      <div className="dep-head">
        <h1 className="page-title">Đơn #{order.id}</h1>
        <StatusBadge status={order.status} />
      </div>
      <Flash />

      <div className="checkout-layout">
        <div className="checkout-main">
          {isBfm && !paid && order.status === 'chua_thanh_toan' ? (
            gem >= order.total ? (
              <div className="panel checkout-gem">
                <p>
                  <i className="fa-regular fa-gem" aria-hidden /> Ví gem <strong>{gem.toLocaleString('vi-VN')}</strong> — đủ thanh
                  toán đơn này.
                </p>
                {gemErr ? <p className="note">{gemErr}</p> : null}
                <button type="button" className="btn btn-sm bfm-btn-gem" onClick={() => payGem(order)} disabled={gemBusy}>
                  {gemBusy ? 'Đang thanh toán…' : 'Thanh toán bằng ví gem'}
                </button>
              </div>
            ) : (
              <p className="checkout-gem-low muted">
                Ví gem {gem.toLocaleString('vi-VN')} — cần thêm {(order.total - gem).toLocaleString('vi-VN')} gem hoặc chuyển
                khoản bên dưới. <Link to="/deposit">Nạp gem</Link>
              </p>
            )
          ) : null}

          {delivered ? (
            <div className="panel checkout-keys">
              <h2>
                <i className="fa-solid fa-key" aria-hidden /> Key của bạn
              </h2>
              <p className="muted" style={{ marginTop: 0 }}>
                Nhập key tại{' '}
                <a href="https://redeem.microsoft.com" target="_blank" rel="noreferrer">
                  redeem.microsoft.com
                </a>
              </p>
              <ul className="checkout-key-list">
                {keys.map((key) => (
                  <li key={key}>
                    <code>{key}</code>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => copyKey(key)}
                    >
                      <i className="fa-solid fa-copy" aria-hidden />{' '}
                      {copied === key ? 'Đã copy' : 'Copy'}
                    </button>
                  </li>
                ))}
              </ul>
              <p style={{ marginBottom: 0 }}>
                <Link to={`/account/don/${order.id}`}>
                  <i className="fa-solid fa-receipt" aria-hidden /> Chi tiết đơn & đánh giá
                </Link>
              </p>
            </div>
          ) : null}

          {!paid && pay ? (
            <div className="panel dep-pay">
              <div className="dep-qr">
                <img src={pay.vietqr_url} alt="VietQR" referrerPolicy="no-referrer" />
                <p className="muted">Mở app ngân hàng & quét mã</p>
              </div>
              <div className="dep-info">
                <h2>{pay.bank_name}</h2>
                <dl>
                  <div>
                    <dt>Số tiền</dt>
                    <dd>
                      <strong>{formatVnd(pay.amount)}</strong>
                      <CopyBtn text={String(pay.amount)} />
                    </dd>
                  </div>
                  <div>
                    <dt>Nội dung CK</dt>
                    <dd>
                      <code className="dep-code">{pay.content}</code>
                      <CopyBtn text={pay.content} />
                    </dd>
                  </div>
                  <div>
                    <dt>Số tài khoản</dt>
                    <dd>
                      {pay.account_no}
                      <CopyBtn text={pay.account_no} />
                    </dd>
                  </div>
                  <div>
                    <dt>Chủ tài khoản</dt>
                    <dd>{pay.account_name}</dd>
                  </div>
                </dl>
                <p className="muted dep-wait">
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Đang chờ chuyển khoản…
                </p>
              </div>
            </div>
          ) : null}

          {paid && QUEUE_TYPES.includes(order.type) ? (
            <p className="panel checkout-note">
              <i className="fa-solid fa-circle-check" aria-hidden /> Đã nhận đơn ·{' '}
              <Link to="/account/dat-ho">Xem đơn đặt hộ</Link>
            </p>
          ) : null}

          {paid && !delivered && !QUEUE_TYPES.includes(order.type) ? (
            <p className="panel checkout-note muted">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Đang giao key…
            </p>
          ) : null}
        </div>

        <aside className="panel checkout-summary dep-info">
          <h2>Đơn hàng</h2>
          <dl>
            {order.name ? (
              <div>
                <dt>Sản phẩm</dt>
                <dd>{order.name}</dd>
              </div>
            ) : null}
            {isBfm ? (
              <div>
                <dt>Tên nhân vật</dt>
                <dd>{order.character_name || '—'}</dd>
              </div>
            ) : null}
            <div>
              <dt>Số lượng</dt>
              <dd>{order.quantity}</dd>
            </div>
            {order.discount > 0 ? (
              <div>
                <dt>Giảm giá</dt>
                <dd>−{formatVnd(order.discount)}</dd>
              </div>
            ) : null}
            <div className="checkout-total">
              <dt>Tổng tiền</dt>
              <dd>{formatVnd(order.total)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </div>
  )
}
